import { BookingContactForm } from "@molecules/booking-contact-form";
import { BookingContactFormData } from "@molecules/booking-contact-form/types";
import { ContactThanks } from "@molecules/contact-thanks";
import { AnimatePresence, motion } from "framer-motion";
import React, { FC, useCallback, useState } from "react";
import styled, { css } from "styled-components";

export type ContactSectionFormProps<T extends {}> = {
  sending: boolean;
  onSubmit: (form: T) => void;
  initialData?: Partial<T>;
};

export type ContactSectionProps<TData extends {}> = {
  className?: string;
  highlightBackground?: boolean;
  initialData?: Partial<TData>;
  form: React.ComponentType<ContactSectionFormProps<TData>>;
  onSubmit: (form: TData) => Promise<boolean>;
};

export function ContactSection<TData extends {}>(
  props: ContactSectionProps<TData>
) {
  const {
    className,
    highlightBackground,
    onSubmit,
    initialData,
    form: Form,
  } = props;
  const [sending, setSending] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  const handleSubmit = useCallback(
    async (form: TData) => {
      setSending(true);
      const result = await onSubmit(form);
      if (result) {
        setShowThanks(true);
      } else {
        alert("An error ocurred sending message, try later");
      }
      setSending(false);
    },
    [onSubmit]
  );

  return (
    <Container highlightBackground={highlightBackground} className={className}>
      <AnimatePresence initial={false} exitBeforeEnter>
        {showThanks && (
          <motion.section
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ContactThanks />
          </motion.section>
        )}
        {!showThanks && (
          <motion.section
            key="thanks"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Form
              onSubmit={handleSubmit}
              sending={sending}
              initialData={initialData}
            />
          </motion.section>
        )}
      </AnimatePresence>
    </Container>
  );
}
const Container = styled.div.attrs<{ highlightBackground?: boolean }>(
  (props) => ({ highlightBackground: props.highlightBackground })
)`
  ${({ highlightBackground }) =>
    highlightBackground === true &&
    css`
      background-color: #f9f9f9;
      padding: 1em 1em 2em 1em;
      @media (min-width: 48em) {
        padding: 1em 3em 2em 3em;
      }
    `};
`;
